import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgbProgressbarModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { SkillDisplayNamePipe } from '../../pipes/skill-display-name.pipe';

@Component({
  selector: 'app-guard-details-modal',
  imports: [TranslateModule, NgbProgressbarModule, MatTabsModule, CommonModule, MatTooltipModule, SkillDisplayNamePipe],
  templateUrl: './guard-details-modal.component.html',
  styleUrl: './guard-details-modal.component.scss',
  standalone: true
})
export class GuardDetailsModalComponent implements OnInit {
  guardDetails: any = []
  mergedWorkExperiences: any[] = [];
  expandedCompanies: Set<string> = new Set();

  constructor(@Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<GuardDetailsModalComponent>,) { }

  ngOnInit(): void {
    this.guardDetails = this.data
    if (this.guardDetails?.workExperiences?.length) {
      this.mergedWorkExperiences = this.mergeCompanySkills(
        this.guardDetails.workExperiences
      );
    }
  }

  closeModal() {
    this.dialogRef.close();
  }

  formatSwedishPhone(phone: string): string {
    if (!phone) return '-';

    const normalized = phone.trim().replace(/\s+/g, '');
    const isInternational = normalized.startsWith('+');

    // Keep digits only, preserve leading '+'
    const digits = isInternational
      ? '+' + normalized.slice(1).replace(/\D/g, '')
      : normalized.replace(/\D/g, '');

    // Format international: +46XXXXXXXXXXX -> +46 xxx xxx xxx xxx
    if (digits.startsWith('+46')) {
      const rest = digits.slice(3); // Remove '+46'
      const groups = rest.match(/\d{1,3}/g); // Split every 1–3 digits
      return '+46 ' + (groups ? groups.join(' ') : rest);
    }

    // Format local (e.g. 0171000559708) as xxx xxx xxx xxx
    if (digits.length > 6 && digits.startsWith('0')) {
      const groups = digits.match(/\d{1,3}/g);
      return groups ? groups.join(' ') : digits;
    }
    // Fallback
    return phone;
  }

  isValidBiography(bio: any): boolean {
    // Check if bio exists and is a non-empty string
    if (typeof bio === 'string' && bio.trim().length > 0) {
      return true;
    }
    // Also, if it's an object but empty, treat as invalid
    if (typeof bio === 'object' && bio !== null && Object.keys(bio).length === 0) {
      return false;
    }
    // If it's anything else, treat as invalid
    return false;
  }

  trackByRate(index: number, rate: any) {
    return rate.companyName || index;
  }

  // Function for display avergae rating
  get displayAverageRating(): string {
    if (
      this.guardDetails &&
      this.guardDetails.averageRating &&
      Object.keys(this.guardDetails.averageRating).length > 0
    ) {
      return this.guardDetails.averageRating;
    }
    return '–';
  }

  //Function for averageRating has data or not
  get hasAverageRating(): boolean {
    const rating = this.guardDetails?.averageRating;
    return rating && typeof rating === 'object' && Object.keys(rating).length > 0;
  }

  //Function for image error
  onImageError(event: Event): void {
    const imgElement = event.target as HTMLImageElement;
    imgElement.src = '../../assets/img/safetly_logo_re.png';
  }

  expandedComments: boolean[] = [];

  toggleComment(index: number): void {
    this.expandedComments[index] = !this.expandedComments[index];
  }

  /** Stable key for grouping / expand state; avoids null companyName and non-string values. */
  private companyMergeKey(item: any, index: number): string {
    const raw = item?.companyName;
    if (raw != null && String(raw).trim() !== '') {
      return String(raw).trim().toLowerCase();
    }
    return `__unnamed__${index}`;
  }

  private experienceExpandKey(experience: any): string {
    if (experience?.mergeKey) {
      return experience.mergeKey;
    }
    return this.companyMergeKey(experience, 0);
  }

  private mergeCompanySkills(experiences: any[]) {
    const toArray = (v: any): any[] => (Array.isArray(v) ? v : []);

    const merged = Object.values(
      experiences
        .filter(item => item != null)
        .reduce((acc, item, index) => {
          const key = this.companyMergeKey(item, index);
          const itemSkills = toArray(item.skills);

          if (!acc[key]) {
            acc[key] = { ...item, skills: [...itemSkills], mergeKey: key };
          } else {
            const existing = toArray(acc[key].skills);
            acc[key].skills = [...new Set([...existing, ...itemSkills])];
          }

          return acc;
        }, {} as Record<string, any>)
    );

    return merged;
  }

  //Display default 3 skills at a time after click on show all display all skills
  toggleSkills(experience: any) {
    const key = this.experienceExpandKey(experience);
    if (this.expandedCompanies.has(key)) {
      this.expandedCompanies.delete(key);
    } else {
      this.expandedCompanies.add(key);
    }
  }

  isExpanded(experience: any): boolean {
    return this.expandedCompanies.has(this.experienceExpandKey(experience));
  }

  getCombinedFeedback(feedback: any, key: 'good' | 'excellent' | 'poor' | 'total'): number {
    if (!feedback) return 0;

    const teamwork = feedback.teamwork || {};
    const communication = feedback.communication || {};

    if (key === 'total') {
      return (teamwork.teamworkTotal || 0) + (communication.communicationTotal || 0);
    }

    return (teamwork[key] || 0) + (communication[key] || 0);
  }

  getFeedbackPercentage(feedback: any, key: 'good' | 'excellent' | 'poor'): string {
    const total = this.getCombinedFeedback(feedback, 'total');
    if (!total) return '0%';
    const value = this.getCombinedFeedback(feedback, key);
    return ((value / total) * 100).toFixed(0) + '%';
  }


  getAverageRating(rates: any[]): number {
    if (!rates || rates.length === 0) return 0;
    const total = rates.reduce((sum, r) => sum + (r.rate || 0), 0);
    const avg = total / rates.length;
    return parseFloat(avg.toFixed(1)); // Ensures output such as 3.5 instead of 3,5
  }

}
